import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ContactButton } from '../components';
import mediaQueries from '../styles/mediaQueries';
import { jobs } from '../data/jobs';

const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  font-family: var(--font-primary);
  padding: 0 20px 0px;
`;

const MainSection = styled.main`
  max-width: 1000px;
  margin: 0 auto;
`;

const HeroSection = styled.section`
  padding: 60px 100px 0px;
  margin-bottom: 40px;
  
  ${mediaQueries.large} {
    padding: 50px 100px;
  }
  
  ${mediaQueries.medium} {
    padding: 40px 40px;
  }
  
  ${mediaQueries.small} {
    padding: 30px 20px;
  }
`;

const TitleContainer = styled.div`
  margin-bottom: 40px;
  text-align: right;
  line-height: 1.1;
`;

const PageTitle = styled.h1`
  font-size: 40px;
  font-weight: 800;
  margin-bottom: 0px;

  ${mediaQueries.medium} {
    font-size: 36px;
  }
`;

const PageSubtitle = styled.h2`
  font-size: 40px;
  font-weight: 700;
  margin-bottom: 30px;

  ${mediaQueries.medium} {
    font-size: 36px;
  }
`;

const HighlightedText = styled.span`
  color: ${({theme}) => theme.colors.secondary};
  font-weight: 700;
`;

const Description = styled.p`
  font-size: 18px;
  line-height: 1.6;
  max-width: 800px;
  margin-bottom: 40px;

  ${mediaQueries.medium} {
    font-size: 16px;
  }
`;

const TalentsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 0 60px;
  
  ${mediaQueries.medium} {
    padding: 0 15px;
  }
`;

const JobsSection = styled.section`
  padding: 40px;
  background-color: #FEF2F1;
  border-radius: 32px;
  margin: 0 auto 40px;
  
  ${mediaQueries.large} {
    padding: 40px 30px;
  }
  
  ${mediaQueries.medium} {
    padding: 30px 20px;
  }
  
  ${mediaQueries.small} {
    padding: 20px 15px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  margin-bottom: 0px;
  text-align: center;
  width: 100%;
  color: ${({theme}) => theme.colors.secondary};
  margin-bottom: 40px;

`;

const SectionSubtitle = styled.h3`
  font-size: 32px;
  text-align: center;
  width: 100%;
  color: ${({theme}) => theme.colors.primary};
`;

const JobCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 20px;
  transition: all 0.3s;
  
  &:hover {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    border-color: ${({theme}) => theme.colors.secondary};
  }
`;

const JobHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const JobTitle = styled.h3`
  font-size: 20px;
  font-weight: var(--font-weight-semibold);
`;

const JobMeta = styled.div`
  display: flex;
  gap: 20px;
  color: #666;
  font-size: 14px;
  
  span {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

const JobDescription = styled.p`
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
  color: #333;
`;

const JobButton = styled(Link)`
  display: inline-block;
  padding: 8px 16px;
  background-color: #fff;
  color: ${({theme}) => theme.colors.secondary};
  border: 1px solid ${({theme}) => theme.colors.secondary};
  border-radius: 20px;
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  text-decoration: none;
  transition: all 0.3s;
  
  &:hover {
    background-color: ${({theme}) => theme.colors.secondary};
    color: white;
  }
`;

const TeamSection = styled.section`
  margin: 80px 0;
  text-align: center;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const TeamMember = styled.div`
  img {
    width: 180px;
    height: 180px;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 15px;
  }
  
  h3 {
    font-size: 18px;
    margin-bottom: 5px;
    font-weight: var(--font-weight-semibold);
  }
  
  p {
    font-size: 14px;
    color: #666;
    margin-bottom: 10px;
  }
`;

const ContactSection = styled.section`
  background-color: #f5f5f5;
  border-radius: 16px;
  padding: 60px;
  text-align: center;
  margin: 60px 0;
`;

const ContactTitle = styled.h2`
  font-size: 36px;
  margin-bottom: 20px;
  font-weight: var(--font-weight-semibold);
  color: ${({theme}) => theme.colors.secondary};
`;

const Button = styled.a`
  background-color: ${({theme}) => theme.colors.secondary};
  color: white;
  padding: 12px 24px;
  border-radius: 32px;
  text-decoration: none;
  display: inline-block;
  font-weight: var(--font-weight-medium);
  margin-top: 20px;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #c42b12;
  }
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 24px 0 32px;
`;

const ChipGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-right: 24px;
`;

const ChipGroupTitle = styled.h3`
  font-size: 14px;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
`;

const ChipsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  background-color: #f0f0f0;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  
  &.backend {
    background-color: #E3F2FD;
    color: #0D47A1;
  }
  
  &.frontend {
    background-color: #FFF8E1;
    color: #FF8F00;
  }
  
  &.devops {
    background-color: #E8F5E9;
    color: #2E7D32;
  }
  
  &.tools {
    background-color: #F3E5F5;
    color: #7B1FA2;
  }
`;

const NoJobsMessage = styled.div`
  text-align: center;
  padding: 00px 00px;
  border-radius: 16px;
  margin: 0 auto;
  max-width: 600px;
`;

const NoJobsTitle = styled.h3`
  font-size: 24px;
  color: ${({theme}) => theme.colors.primary};
  margin-bottom: 16px;
  font-weight: var(--font-weight-semibold);
`;

const NoJobsDescription = styled.p`
  font-size: 17px;
  line-height: 1.6;
  color: #000000;
  margin-bottom: 24px;
`;

const NoJobsButton = styled.div`
  display: flex;
  justify-content: center;
`;

const Recrutement: React.FC = () => {
  const currentJobs = jobs;
  return (
    <PageContainer>
      <SEO 
        title="Rejoignez notre équipe" 
        description="Thinkfab recrute des talents passionnés. Découvrez nos offres d'emploi et d'alternance dans le développement web."
        canonical={`${window.location.origin}/recrutement`}
        image="/images/home/wdd-wearepeople.png"
      />
      <Header backgroundColor={"transparent"} textColor={"#011D3A"} /> {/* Fond transparent avec texte bleu */}
      
      <MainSection>
        <HeroSection>
          <TitleContainer>
            <PageTitle>
              <HighlightedText>We connect talents.</HighlightedText>
            </PageTitle>
            <PageSubtitle>Rejoignez l'équipe.</PageSubtitle>
          </TitleContainer>
          
          <Description>
            Chez Thinkfab, nous recherchons des personnes passionnées, curieuses et créatives. 
            Notre équipe valorise l'initiative, l'apprentissage continu et le partage de connaissances.
          </Description>
          
          <Description>
            Nous offrons un environnement propice à l'épanouissement dans le travail avec la volonté 
            constante d'apprendre et progresser. <strong>C'est bon pour le groupe, pour soi-même et pour les projets de nos clients.</strong>
          </Description>
        </HeroSection>
        
        <TalentsContent>
          
          <JobsSection>
            <SectionTitle>
              {currentJobs.length > 0 ? "Nos opportunités actuelles." : "Nous sommes toujours à la recherche de talents."}
            </SectionTitle>
            
            {currentJobs.length > 0 ? (
              currentJobs.map(job => (
                <JobCard key={job.id}>
                  <JobHeader>
                    <JobTitle>{job.title}</JobTitle>
                    <Chip className="tools">{job.type}</Chip>
                  </JobHeader>
                  
                  <JobDescription>
                    {job.description}
                  </JobDescription>
                  
                  <JobButton to={job.link}>Consulter l'offre</JobButton>
                </JobCard>
              ))
            ) : (
              <NoJobsMessage>
                <NoJobsDescription>
                  Même si aucune offre n'est actuellement publiée, nous restons ouverts aux candidatures spontanées. 
                  N'hésitez pas à nous contacter pour discuter de vos compétences et de vos aspirations.
                </NoJobsDescription>
                <NoJobsButton>
                  <ContactButton variant="primary">Nous contacter</ContactButton>
                </NoJobsButton>
              </NoJobsMessage>
            )}

          </JobsSection>

        </TalentsContent>
      </MainSection>
      
      <Footer 
        pageContext="Page Recrutement"
        heading="Rejoignez notre équipe !"
        subheading="Postulez dès maintenant et commencez une nouvelle aventure"
        buttonText="Envoyer votre candidature"
      />
    </PageContainer>
  );
};

export default Recrutement;